import { useEffect, useState } from 'react';
import './App.css';
import { Button, ConfigProvider } from 'antd';
import { ISingleReservation, IWholeHallReservation, locale as applocale, t } from './MainApp';
import { SingleHallReservation } from './singlehallreservation/SingleHallReservation';
import { ReservationMadeWindow } from './sharedcomponents/ReservationMadeWindow';
import { Breadcrumbs } from './sharedcomponents/Breadcrumb';
import { HallSelector } from './sharedcomponents/HallSelector';
import { WholeHallReservation } from './wholehallreservation/WholeHallReservation';
import { useNavigate } from 'react-router-dom';

import enUS from 'antd/es/locale/en_US';
import bgBG from 'antd/es/locale/bg_BG';
import 'dayjs/locale/bg';

export interface IMember {
  username: string,
  fullName: string,
  currentMembershipId: number,
  hasChild: boolean,
}

export interface WorkTimeForADay {
  dayOfWeek: number,
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
}

export type IObject = {
  id: number;
  name: string;
  halls: IHall[],
  worktime: WorkTimeForADay[],
  interval: number,
  allowedMemberships: number[],
  hasFreeVisits: boolean,
  remainingFreeVisits: number,
}

export type IHall = {
  id: number;
  name: string;
  tables: ITable[];
  capacity: number;
  rezervationSizeLimit: number;
  maximumDurationInMinutes: number;
  allowWholeHallReservation: boolean;
  denySingleHallReservation: boolean;
  minimumDuration?: number;
}

export type IBoundingRectangle = {
  x: number;
  y: number;
  w: number;
  h: number;
};

export type ITable = {
  id: number;
  name: string;
  reservations: ITableFreeTime[];
  boundingRectangle?: IBoundingRectangle;
  seats: number,
};

export type ITableFreeTime = {
  startDate: number;
  endDate: number;
}

export type ISingleHallOccupancyData = {
  label: string,
  persons: number,
}

export type IHallOccupancyData = {
  maxPersons: number,
  data: ISingleHallOccupancyData[],
};

enum ReservationMode {
  Single,
  Whole,
};

type AppProperties = {
  object: IObject,
  locale: applocale,
  member: IMember,
  reservation?: ISingleReservation,
  wholeHallReservation?: IWholeHallReservation,
  onMakeOrUpdateReservation: () => void,
  cybarObject?: IObject,
};

export function App(props: AppProperties) {
  let [selectedHall, setSelectedHall] = useState<IHall>(props.object.halls[0]);
  let [reservationMode, setReservationMode] = useState<ReservationMode>(ReservationMode.Single);
  let [finishedMakingOrEditing, setFinishedMakingOrEditing] = useState<boolean>(false);
  let navigate = useNavigate();

  useEffect(() => {
    if(selectedHall.denySingleHallReservation) {
      if(selectedHall.allowWholeHallReservation) {
        setReservationMode(ReservationMode.Whole);
      } else {
        // no mode available
      }
    } else {
      setReservationMode(ReservationMode.Single);
    }
  }, [selectedHall]);

  useEffect( () => {
    if(props.reservation) {
      const hall = props.object.halls.find( (value) => {
        return value.id === props.reservation?.hallId;
      });

      if(hall) {
        setReservationMode(ReservationMode.Single);
        setSelectedHall(hall);
      }      
    }

    if(props.wholeHallReservation) {
      const hall = props.object.halls.find( (value) => {
        return value.id === props.wholeHallReservation?.reservations[0].hallId;
      });

      if(hall) {
        setReservationMode(ReservationMode.Whole);
        setSelectedHall(hall);
      }      
    }
  }, [props.object.halls, props.reservation, props.wholeHallReservation]);



  return (
    <ConfigProvider locale={props.locale === 'en' ? enUS : bgBG}>

      {!finishedMakingOrEditing ?
        <>
          <Breadcrumbs
            isMakingNewReservation={!props.reservation && !props.wholeHallReservation}
            locale={props.locale}
          />

          {props.wholeHallReservation ? <></> :
          selectedHall.denySingleHallReservation ? <></> : <Button data-testid="btn-single-reservation-mode" type='primary' disabled={props.wholeHallReservation !== undefined} style={{ width: !selectedHall.denySingleHallReservation && selectedHall.allowWholeHallReservation ? '50%' : '100%' }} onClick={(event) => {
            setReservationMode(ReservationMode.Single);
          }}>{t(props.locale, "modesinglereservation")}</Button>
          }
          
          {props.reservation ? <></> : 
          selectedHall.allowWholeHallReservation ?
            <Button 
              data-testid="btn-whole-reservation-mode" 
              danger 
              style={{ width: (selectedHall.denySingleHallReservation && selectedHall.allowWholeHallReservation) || props.wholeHallReservation ? '100%' : '50%' }} 
              disabled={!selectedHall.allowWholeHallReservation || props.wholeHallReservation !== undefined}
              onClick={(event) => {
              setReservationMode(ReservationMode.Whole);
            }}>{t(props.locale, "modewholehallreservation")}</Button>
            : <></>
          }

          <HallSelector
            disabled={props.reservation || props.wholeHallReservation ? true : false}
            object={props.object}
            onSelectHall={(hall) => setSelectedHall(hall)}
            selectedHall={selectedHall}
          />

          { ((reservationMode === ReservationMode.Single) && (!selectedHall.denySingleHallReservation && !props.wholeHallReservation)) || props.reservation ?
            <SingleHallReservation
              reservation={props.reservation}
              selectedHall={selectedHall}
              locale={props.locale}
              member={props.member}
              object={props.object}
              onMakeReservation={(rid) => {
                props.onMakeOrUpdateReservation();
                setFinishedMakingOrEditing(true);
              }}
              onUpdateReservation={(rid) => {
                props.onMakeOrUpdateReservation();
                setFinishedMakingOrEditing(true);
              }}
            />
            :
            ((reservationMode === ReservationMode.Whole ) && selectedHall.allowWholeHallReservation) || props.wholeHallReservation ?
              <WholeHallReservation
                locale={props.locale}
                object={props.object}
                selectedHall={selectedHall}
                onMakeReservation={(rid) => {
                  setReservationMode(ReservationMode.Single);
                  setFinishedMakingOrEditing(true);
                  props.onMakeOrUpdateReservation();
                }}
                existingWholeHallReservation={props.wholeHallReservation}
                cybarWorktime={props.cybarObject?.worktime}
              />
              : <></>
          }
        </>
        :
        <ReservationMadeWindow
          locale={props.locale}
          onMakeAnotherReservation={() => {
            if(!selectedHall.denySingleHallReservation) {
              setReservationMode(ReservationMode.Single);
            } else {
              if(selectedHall.allowWholeHallReservation) {
                setReservationMode(ReservationMode.Whole);
              }
            }
            
            setFinishedMakingOrEditing(false);
            props.onMakeOrUpdateReservation();
            navigate(`/`);
          }}
          reservation={props.reservation}
          wholeHallReservation={props.wholeHallReservation}
        />
      }
    </ConfigProvider>
  );
}